import { getHours, getMinutes, isSameDay } from 'date-fns';
import { EvaluationCalendarDay } from 'domains/calendars/v3/useEvaluationCalendarSummary';
import { uniqBy } from 'lodash';

export interface TherapistEmailResult {
  therapistEmail: string;
  therapistName: string;
  calendarID: string;
}

/**
 * Returns therapist emails that have availability for a specific date and time
 * @param availability - The evaluation calendar data
 * @param selectedDate - The selected date and time
 * @returns Array of therapist emails with their details
 */
export const getTherapistEmailsByDateTime = (
  availability: { currentAvailability?: EvaluationCalendarDay; allTimeSlots?: EvaluationCalendarDay[] },
  selectedDate: Date
): TherapistEmailResult[] => {
  // Return empty array if no availability data
  if (!availability.allTimeSlots || availability.allTimeSlots.length === 0) {
    return [];
  }

  // Find the specific day that matches the selected date
  const selectedDay = availability.allTimeSlots.find(day => 
    isSameDay(new Date(day.day), selectedDate)
  );

  // If no matching day found, return empty array
  if (!selectedDay || !selectedDay.availabilityByTherapist) {
    return [];
  }

  // Get hour and minute from selected date for comparison
  const selectedHour = getHours(selectedDate);
  const selectedMinute = getMinutes(selectedDate);

  // Check all therapists' availability for the specific time
  const therapistEmailsMap = new Map<string, TherapistEmailResult>();

  selectedDay.availabilityByTherapist.forEach(therapistAvailability => {
    // Check if this therapist has a slot at the selected time
    const matchingSlot = therapistAvailability.timeSlots.find(timeSlot => {
      const slotHour = getHours(timeSlot.time);
      const slotMinute = getMinutes(timeSlot.time);
      return slotHour === selectedHour && slotMinute === selectedMinute;
    });

    if (matchingSlot && !therapistEmailsMap.has(matchingSlot.therapistID)) {
      therapistEmailsMap.set(matchingSlot.therapistID, {
        therapistEmail: matchingSlot.therapistID,
        therapistName: matchingSlot.therapistName,
        calendarID: matchingSlot.calendarID,
      });
    }
  });

  const uniqueTherapists = uniqBy(Array.from(therapistEmailsMap.values()), 'calendarID');
  return uniqueTherapists;
}; 